import { useEffect, useRef } from 'react';

const POLLING_INTERVAL_MS = 30000; // 30 segundos

interface UseSequentialPollingParams {
  fetchFn: () => Promise<unknown>;
  shouldPoll: boolean;
  enabled: boolean;
  skipFirstImmediate?: boolean;
}

export const useSequentialPolling = ({
  fetchFn,
  shouldPoll,
  enabled,
  skipFirstImmediate = false,
}: UseSequentialPollingParams): void => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);

  useEffect(() => {
    const canPoll = enabled && shouldPoll;

    if (!canPoll) {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      isPollingRef.current = false;
      return;
    }

    if (isPollingRef.current) {
      return;
    }

    isPollingRef.current = true;

    const executePoll = async (): Promise<void> => {
      try {
        await fetchFn();
      } catch {
        // Ignoramos errores para continuar el polling
      }

      if (isPollingRef.current) {
        timeoutIdRef.current = setTimeout(() => {
          executePoll();
        }, POLLING_INTERVAL_MS);
      }
    };

    if (skipFirstImmediate) {
      timeoutIdRef.current = setTimeout(() => {
        executePoll();
      }, POLLING_INTERVAL_MS);
    } else {
      executePoll();
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [fetchFn, shouldPoll, enabled, skipFirstImmediate]);
};
